import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErreurApi } from '../api';
import { redirectionConnexionEnCours } from './session';

function estSessionExpiree(erreur: unknown): boolean {
  return erreur instanceof ErreurApi && erreur.statut === 401 && !redirectionConnexionEnCours();
}

/** Adresse de connexion qui ramène sur l'écran courant (URL complète, filtres compris). */
export function adresseConnexion(): string {
  const retour = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return `/connexion?retour=${encodeURIComponent(retour)}`;
}

/**
 * Modale de reconnexion (SPEC 4, règle 12). Elle s'ouvre dès qu'une requête ou une mutation reçoit
 * 401. La connexion se fait dans un nouvel onglet : l'écran courant, son URL et les brouillons en
 * mémoire restent intacts. Ensuite "J'ai terminé" vérifie la session et relance les requêtes.
 */
export function ModaleReconnexion() {
  const client = useQueryClient();
  const [ouverte, setOuverte] = useState(false);
  const [verification, setVerification] = useState(false);
  const [echec, setEchec] = useState(false);

  useEffect(() => {
    const surRequete = client.getQueryCache().subscribe((evenement) => {
      if (evenement.type === 'updated' && evenement.action.type === 'error') {
        if (estSessionExpiree(evenement.action.error) && window.location.pathname !== '/connexion') {
          setOuverte(true);
        }
      }
    });
    const surMutation = client.getMutationCache().subscribe((evenement) => {
      if (evenement.type === 'updated' && evenement.action.type === 'error') {
        if (estSessionExpiree(evenement.action.error) && window.location.pathname !== '/connexion') {
          setOuverte(true);
        }
      }
    });
    return () => {
      surRequete();
      surMutation();
    };
  }, [client]);

  const reprendre = async () => {
    setVerification(true);
    setEchec(false);
    try {
      const reponse = await fetch('/api/moi', { credentials: 'same-origin' });
      if (reponse.ok) {
        setOuverte(false);
        await client.invalidateQueries();
      } else {
        setEchec(true);
      }
    } catch {
      setEchec(true);
    } finally {
      setVerification(false);
    }
  };

  return (
    <Dialog.Root open={ouverte}>
      <Dialog.Portal>
        <Dialog.Overlay className="voile fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          className="surgissement fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-bord bg-surface p-5 shadow-xl"
        >
          <Dialog.Title className="text-lg font-semibold">Votre session a expiré</Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-doux">
            Reconnectez-vous dans un nouvel onglet. Cet écran, vos filtres et votre brouillon sont conservés :
            vous reprendrez exactement où vous en étiez.
          </Dialog.Description>
          {echec && (
            <p role="alert" className="mt-3 text-sm text-erreur">
              La session n'est pas encore rétablie. Terminez la connexion dans l'autre onglet, puis réessayez.
            </p>
          )}
          <div className="mt-5 flex flex-col gap-2">
            <a
              href={adresseConnexion()}
              target="_blank"
              rel="noopener"
              className="cible inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-4 font-medium text-[var(--accent-texte)]"
            >
              <LogIn size={18} aria-hidden /> Se reconnecter
              <span className="sr-only"> (nouvel onglet)</span>
            </a>
            <button
              type="button"
              onClick={reprendre}
              disabled={verification}
              className="cible rounded-lg border border-bord px-4 font-medium disabled:opacity-60"
            >
              {verification ? 'Vérification' : "J'ai terminé, reprendre"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
